import * as React from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { Skeleton } from "@/ui/primitives/skeleton";
import { LGoButton } from "@/ui/branded/LGoButton";
import { EmptyState } from "./EmptyState";

export interface AsyncBoundaryProps<T> {
  data: T | undefined | null;
  isLoading: boolean;
  error: unknown;
  /** Mirrors the query's own `enabled` option. A query a prerequisite kept
   *  from ever starting reports `isLoading: false`, `error: null`, `data:
   *  null` — indistinguishable from one still in flight unless the caller
   *  says so here. Defaults to `true`, so callers that never pass it keep
   *  today's behavior. */
  enabled?: boolean;
  isEmpty?: (data: T) => boolean;
  loadingFallback?: React.ReactNode;
  errorFallback?: (error: unknown, retry?: () => void) => React.ReactNode;
  emptyFallback?: React.ReactNode;
  onRetry?: () => void;
  children: (data: T) => React.ReactNode;
}

function defaultLoading() {
  return (
    <div className="flex flex-col gap-3 py-4" aria-live="polite" aria-busy="true">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

const genericErrorMessage = "Něco se pokazilo. Zkuste to prosím znovu.";
const notRunMessage = "Tyto údaje se nepodařilo zjistit. Zkuste to prosím znovu.";

/** `PortalRefusal` (`data/api/client.ts`) carries a sentence already written
 *  for the partner and must reach the screen unchanged. Checked by `name`
 *  rather than `instanceof` because importing the class here would cross
 *  from `ui/` into `data/`, which the layering forbids. Every other error —
 *  a `TypeError`, a raw string, an `ApiError` with no portal-worded code —
 *  is a stack-trace sentence, not something a partner can act on. */
export function messageForPartner(error: unknown): string | null {
  return error instanceof Error && error.name === "PortalRefusal" ? error.message : null;
}

function ActionableNotice({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-3 py-10" role="alert">
      <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden="true" />
      <p className="text-sm text-muted-foreground max-w-prose">{message}</p>
      {retry && (
        <LGoButton variant="secondary" size="sm" onClick={retry}>
          Zkusit znovu
        </LGoButton>
      )}
    </div>
  );
}

function DefaultError({ error, retry }: { error: unknown; retry?: () => void }) {
  React.useEffect(() => {
    console.error("[AsyncBoundary]", error);
  }, [error]);
  return <ActionableNotice message={messageForPartner(error) ?? genericErrorMessage} retry={retry} />;
}

/**
 * Single source of truth for loading/error/empty branching on async data.
 * State priority: error > isLoading > not-run (enabled=false, no data yet) >
 * isEmpty(data) > children(data).
 *
 * Used by every list / data view in the app — pages must NOT roll their own
 * `if (isLoading) return …` ladders.
 */
export function AsyncBoundary<T>({
  data,
  isLoading,
  error,
  enabled = true,
  isEmpty,
  loadingFallback,
  errorFallback,
  emptyFallback,
  onRetry,
  children,
}: AsyncBoundaryProps<T>) {
  if (error) {
    return <>{errorFallback ? errorFallback(error, onRetry) : <DefaultError error={error} retry={onRetry} />}</>;
  }
  if (isLoading) {
    return <>{loadingFallback ?? defaultLoading()}</>;
  }
  if (!enabled && data == null) {
    return <ActionableNotice message={notRunMessage} retry={onRetry} />;
  }
  if (data == null) {
    return <>{loadingFallback ?? defaultLoading()}</>;
  }
  if (isEmpty && isEmpty(data)) {
    return (
      <>
        {emptyFallback ?? (
          <EmptyState title="Žádná data" description="Tady zatím nic není." />
        )}
      </>
    );
  }
  return <>{children(data)}</>;
}

export { Loader2 as AsyncBoundarySpinner };
